import type { Option, RoundConfiguration } from "Models";
import { roundConfigEnumsAsReactSelectOptions } from "utils/enums/enumHelper";

/**
 * Turns a saved Round Preset into the grouped, labelled settings the View panel
 * shows.
 *
 * `/round-configuration/all` has always returned each preset's full `roundInfo`,
 * but nothing ever displayed it — once a preset was saved, the only way to see
 * what was in it was to start a round with it. This reads the same values back
 * out, using the same option lists the create form labels its selects with, so
 * a preset reads the way it was written.
 *
 * Deliberately omits the simulator plumbing `Round` also carries — macAddress,
 * sessionId, savedGameId, build, players, team counts, holesCompleted. Those
 * are transport for an actual round, not settings anyone chose.
 */

export const PRESET_GROUP_TITLES = [
  "Course",
  "Conditions",
  "Scoring & handicap",
  "Automation",
] as const;
export type PresetGroupTitle = (typeof PRESET_GROUP_TITLES)[number];

type Groups = {
  title: PresetGroupTitle;
  rows: { label: string; value: string }[];
}[];

const OPTS = roundConfigEnumsAsReactSelectOptions;

/**
 * The label the create form would show for this value.
 *
 * Falls back to the raw value rather than hiding the row: a value with no
 * matching option means the enum and the stored data have drifted, which an
 * operator should be able to see rather than have silently blanked.
 */
function labelFor(
  options: Option<string | number | boolean>[] | undefined,
  value: unknown,
): string {
  if (value == null || value === "") return "—";
  const match = options?.find((o) => o.value === value);
  if (match) return match.label;
  // No matching option means the enum and the stored data have drifted. Show
  // the raw value so that's visible, but only when it is one — anything else
  // would stringify as "[object Object]", which tells an operator nothing.
  return typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
    ? String(value)
    : "—";
}

const yesNo = (value: unknown): string =>
  value == null ? "—" : value ? "Yes" : "No";

/**
 * The holes a preset plays. `RoundConfiguration.holes` is the authored list;
 * `roundInfo.holesSelected` is the per-hole boolean form the simulator uses.
 * Either can be the one that's populated, so both are read.
 */
export function holesLabel(config: RoundConfiguration): string {
  const fromList = config.holes?.length ? config.holes : undefined;
  const fromFlags = config.roundInfo?.holesSelected
    ?.map((selected, i) => (selected ? i + 1 : 0))
    .filter((n) => n > 0);
  const holes = fromList ?? (fromFlags?.length ? fromFlags : undefined);
  if (!holes?.length) return "—";

  const front = holes.length === 9 && holes.every((h) => h <= 9);
  const back = holes.length === 9 && holes.every((h) => h > 9);
  if (front) return "Front 9";
  if (back) return "Back 9";
  if (holes.length === 18) return "All 18";
  return `${holes.length} holes (${holes.join(", ")})`;
}

export function roundPresetSummary(config: RoundConfiguration): Groups {
  const round = config.roundInfo;
  if (!round) return [];

  return [
    {
      title: "Course",
      rows: [
        { label: "Course", value: round.courseName || "—" },
        { label: "Holes", value: holesLabel(config) },
        { label: "Men's tee", value: labelFor(OPTS.teeType, round.mensTee) },
        {
          label: "Women's tee",
          value: labelFor(OPTS.teeType, round.womensTee),
        },
      ],
    },
    {
      title: "Conditions",
      rows: [
        {
          label: "Pin difficulty",
          value: labelFor(OPTS.pinType, round.pinType),
        },
        {
          label: "Wind speed",
          value: labelFor(OPTS.windSpeed, round.windSpeed),
        },
        {
          label: "Wind direction",
          value: labelFor(OPTS.windDirection, round.windDirection),
        },
        {
          label: "Sky",
          value: labelFor(OPTS.skyConditions, round.skyConditions),
        },
        {
          label: "Ground",
          value: labelFor(OPTS.groundConditions, round.groundConditions),
        },
        {
          label: "Time of day",
          value: labelFor(OPTS.timeOfDay, round.timeConditions),
        },
        {
          label: "Green speed",
          value: labelFor(OPTS.stimpSetting, round.stimpSetting),
        },
      ],
    },
    {
      title: "Scoring & handicap",
      rows: [
        {
          label: "Scoring",
          value: labelFor(OPTS.scoringType, round.scoringType),
        },
        { label: "Handicaps", value: yesNo(round.handicaps) },
        {
          label: "Handicap mode",
          value: labelFor(OPTS.handicapMode, round.handicapMode),
        },
        {
          label: "Handicap percentage",
          value: labelFor(OPTS.handicapPercentage, round.handicapPercentage),
        },
        {
          label: "Course rating / slope",
          value: yesNo(round.courseRatingSlope),
        },
      ],
    },
    {
      title: "Automation",
      rows: [
        {
          label: "Auto gimmes",
          value: labelFor(OPTS.autoGimme, round.autoGimmes),
        },
        {
          label: "Gimme distance",
          value: labelFor(OPTS.gimmeDistance, round.gimmeDistance),
        },
        {
          label: "Auto concede",
          value: labelFor(OPTS.autoConcede, round.autoConcede),
        },
        {
          label: "Auto continue",
          // Stored top-level on the preset, not in roundInfo — see
          // roundPresetSettings.ts. Reading roundInfo alone showed "—" always.
          value: labelFor(
            OPTS.autoContinue,
            config.autoContinue ?? round.autoContinue,
          ),
        },
        { label: "Auto drop", value: yesNo(round.autoDrop) },
        { label: "Mulligans", value: yesNo(round.mulligans) },
      ],
    },
  ];
}
